/**
 * Descobre as telas dos modulos em build time.
 *
 * `import.meta.glob` com `eager` e resolvido pelo Vite na analise estatica,
 * entao nao ha import dinamico em runtime nem caminho montado por string.
 * Modulo sem tela simplesmente nao aparece, e a rota dele cai no fallback.
 */
import type { TelaDeModulo } from './contrato.ts';

const encontrados = import.meta.glob<{ tela?: TelaDeModulo }>(
  './*/index.tsx', { eager: true });

export const TELAS: readonly TelaDeModulo[] = Object.entries(encontrados)
  .flatMap(([caminho, mod]) => {
    const tela = mod.tela;
    if (tela === undefined) return [];
    // o slug precisa casar com o nome da pasta, senao a rota mente
    const pasta = caminho.split('/')[1];
    if (pasta !== tela.slug) {
      console.warn(
        `[modulos] a pasta "${pasta}" declara slug "${tela.slug}". Tela ignorada.`);
      return [];
    }
    return [tela];
  })
  .sort((a, b) => a.slug.localeCompare(b.slug));

/**
 * As abas da ficha do cliente que os modulos oferecem, com o slug de quem
 * oferece. Funcao e nao constante: Cadastros (que monta a ficha) e carregado
 * por este mesmo arquivo, e ler na hora do uso evita o ciclo de import.
 */
export const abasDaFicha = () => TELAS.flatMap((t) =>
  (t.fichaCliente ?? []).map((aba) => ({ ...aba, modulo: t.slug })));

/** Os blocos da aba Dados da ficha do cliente, com o slug de quem oferece (lido na hora). */
export const blocosDaFicha = () => TELAS.flatMap((t) =>
  (t.blocosDaFicha ?? []).map((b) => ({ ...b, modulo: t.slug })));

/** Os blocos do topo da aba Inicio, com o slug de quem oferece (lido na hora, como a ficha). */
export const blocosDoInicio = () => TELAS.flatMap((t) =>
  (t.blocosDoInicio ?? []).map((b) => ({ ...b, modulo: t.slug })));

export const telaDoSlug = (slug: string): TelaDeModulo | undefined =>
  TELAS.find((t) => t.slug === slug);

/**
 * Junta os dicionarios dos modulos num so, por idioma. Chave que nao comeca
 * com o slug do modulo e descartada: e o que impede um modulo sobrescrever a
 * chave de outro, ou uma chave do nucleo.
 */
export const DICIONARIO_DOS_MODULOS: Record<'pt' | 'en' | 'es', Record<string, string>> =
  (() => {
    const junto = { pt: {}, en: {}, es: {} } as Record<'pt' | 'en' | 'es', Record<string, string>>;
    for (const tela of TELAS) {
      if (tela.dicionario === undefined) continue;
      for (const idioma of ['pt', 'en', 'es'] as const) {
        for (const [chave, valor] of Object.entries(tela.dicionario[idioma] ?? {})) {
          if (!chave.startsWith(`${tela.slug}.`)) {
            console.warn(`[modulos] chave "${chave}" fora do prefixo "${tela.slug}.". Ignorada.`);
            continue;
          }
          junto[idioma][chave] = valor;
        }
      }
    }
    return junto;
  })();
